import { observable, action, runInAction, } from "mobx";
import localforage from "localforage";
import axios from "axios";
import Config from "../modules/dropdownConfig";

class RootStore {
  /** Used for displaying local errors. */
  QUOTE_RESULT_ERROR = {
    success: false,
    message:
      "It seems like we're having connection issues, please try again. If it persists, please contact us directly.",
  };

  @observable
  countryCode = "TT"; //Default Country Code
  @observable
  phonePrefix = "+1 868 ";

  @action
  async setCountry(country) {
    this.countryCode = country;
    const currencyCode = Config.currencyCodes[country];
    await localforage.setItem("currencyCode", currencyCode);
  }

  /** Store the validated form data across steps. */
  validatedFormData = {
    stepOne: {},
    stepTwo: {},
  };

  // Test values
  stepOneCompleted = false;
  stepTwoCompleted = false;

  @observable
  homeQuote = undefined;

  @observable hasHomeQuote = false;

  @observable
  id = undefined;
  
  /** Verify all necessary information has been captured. */
  canFetchHomeQuote() {
    return this.stepOneCompleted && this.stepTwoCompleted;
  }

  /**
   * Send User notification email separately from fetching quote.
   * This allows us to potentially fetch multiple quotes,
   * but only send an email when the user progresses to the
   * "Thanks" page
   */
  async sendUserNotification() {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/home/userConfirmation/${
          this.homeQuote.quote.quoteId
        }`
      );
      if (!res.data.success) {
        throw Error(res.data);
      }
    } catch (err) {
      // No email for this poor sap.
      console.log(err);
    }
  }

  @action
  async fetchHomeQuote() {
    if (this.fetchingHomeQuote) {
      return;
    }
    this.fetchingHomeQuote = true;

    const url = `${process.env.REACT_APP_API_URL}/home/getQuote`;
    const payload = {
      ...this.validatedFormData.stepOne,
      ...this.validatedFormData.stepTwo,
    };

    try {
      const method = this.id ? 'patch' : 'post';
      const res = await axios[method](url, this.id ? { ...payload, id: this.id } : payload);
      runInAction(() => {
        this.homeQuote = res.data;
        this.hasHomeQuote = true;
      });
    } catch (err) {
      runInAction(() => {
        this.homeQuote = this.QUOTE_RESULT_ERROR;
        this.hasHomeQuote = true;
      });
    } finally {
      this.fetchingHomeQuote = false;
    }
  }

  /** Store a copy of provided object. */
  async completeStepOne(countryName, firstName, lastName, email, mobilePhone, producerCode = null) {
    this.validatedFormData.stepOne = {
      email,
      firstName,
      lastName,
      countryName,
      mobilePhone,
      producerCode,
    };
    this.stepOneCompleted = true;
    this.hasHomeQuote = false;
  }

  completeStepTwo(formValues) {
    this.validatedFormData.stepTwo = formValues;
    this.stepTwoCompleted = true;
    this.hasHomeQuote = false;
  }

  @action
  async saveStepOne(values) {
    return this.saveQuote({
      countryName: values.countryName.value,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      mobilePhone: values.mobilePhone,
      producerCode: values.producerCode,
    });
  }

  @action
  async saveStepTwo(values) {
    return this.saveQuote({
      appliancesValue: values.appliancesValue,
      constructionType: values.constructionType.value,
      coverType: values.coverType.value,
      electronicsValue: values.electronicsValue,
      generalContentsValue: values.generalContentsValue,
      jewelryValue: values.jewelryValue,
      location: values.location.value,
      propertyValue: values.propertyValue,
      startMonth: values.startMonth.value,
    });
  }

  @action
  async saveQuote(values) {
    const axios = await import("axios");
    const url = '/api/?category=home';

    if (this.id) {
      try {
        const { data } = await axios.default.request({
          method: 'patch',
          url,
          data: {
            ...values,
            id: this.id,
            quoteId: this.quoteId,
          },
        });
        return data;
      } catch (error) {
        throw error;
      }
    } else {
      try {
        const { data } = await axios.default.request({
          method: 'post',
          url,
          data: values,
        });
        this.id = data.id;
        this.quoteId = data.quoteId;
        return data;
      } catch (error) {
        throw error;
      }
    }
  }
}

export default RootStore;



// WEBPACK FOOTER //
// ./src/store/rootStore.js